import React, { ChangeEvent, useEffect, useMemo, useState } from 'react';

import debounce from 'lodash/debounce';

import { Autocomplete, Box, Stack, TextField, Typography } from '@mui/material';

import { CommissionOption, RuleCondition, ensureAgentHubBrands } from '~/consts/agentHub';

import useCurrentTenant from '~/hooks/useCurrentTenant';

import SearchService, { TypeaheadType } from '~/services/SearchService';

interface Props {
  setCreatedConditions: (condition: RuleCondition, replace?: boolean) => void;
  existingConditions: RuleCondition['value'];
}

const RELEVANT_TYPEAHEAD_TYPES: Array<TypeaheadType> = [
  'province_state',
  'neighborhood',
  'city',
  'high_level_region',
  'country',
  'multi_city_vicinity',
  'continent',
  'point_of_interest',
  'colloquial_area',
  'le_property',
  'le_property_unique_stays',
  'bedbank_property',
  'bedbank_property_unique_stays',
];

/**
 * Location rule condition only (`rules.location`). Flight departure/arrival use
 * {@link AgentHubCommissionsFlightLocationInput}.
 */
function AgentHubCommissionsLocationInput({ setCreatedConditions, existingConditions }: Props) {
  const { tenant } = useCurrentTenant();
  const [loadingState, setLoadingState] = useState<Utils.FetchingState>('idle');
  const [result, setResult] = useState<CommissionOption>([]);
  const [selectedLocations, setSelectedLocations] = useState<CommissionOption>(existingConditions || []);
  const [noOptionsText, setNoOptionsText] = useState<string>('Start typing to search for locations');

  const debounceGetLocations = useMemo(
    () =>
      debounce((search: string) => {
        if (!search) {
          setResult([]);
          return;
        }
        setLoadingState('loading');
        SearchService.typeahead(search, {
          type: RELEVANT_TYPEAHEAD_TYPES,
          brand: ensureAgentHubBrands(tenant.brand),
          region: 'AU',
          limit: 10,
          vertical: 'hotel',
          priority: 'current',
        })
          .then((response) => {
            setResult(
              response.result.map((location) => ({
                value: location.format.mainText,
                label: location.format.mainText,
              })),
            );
            setLoadingState('success');
          })
          .catch(() => {
            setResult([]);
            setNoOptionsText('Failed to fetch location');
          });
      }, 300),
    [tenant.brand],
  );

  useEffect(() => {
    setCreatedConditions(
      {
        type: 'location',
        value: selectedLocations,
      },
      true,
    );
  }, [selectedLocations, setCreatedConditions]);

  const handleInputChange = (_: ChangeEvent, newInputValue: string) => {
    if (!newInputValue) {
      setNoOptionsText('Start typing to search for locations');
      return;
    }
    debounceGetLocations(newInputValue);
  };

  const handleChange = (_: ChangeEvent, value: CommissionOption) => {
    setSelectedLocations(value);
  };

  return (
    <Stack spacing={2}>
      <Box>
        <Autocomplete
          multiple
          loading={loadingState === 'loading'}
          options={result}
          getOptionLabel={(option) => option.label}
          defaultValue={[]}
          filterSelectedOptions
          renderInput={(params) => (
            <TextField {...params} label="Location" placeholder={selectedLocations.length > 0 ? '' : 'Location'} />
          )}
          onInputChange={handleInputChange}
          value={selectedLocations}
          onChange={handleChange}
          noOptionsText={noOptionsText}
          disablePortal
        />
        <Typography variant="caption" color="textSecondary">
          Start typing to search for locations
        </Typography>
      </Box>
    </Stack>
  );
}

export default AgentHubCommissionsLocationInput;
